// DEPRECATED: legacy single-flight 2s-polling for eligibility / breakdown /
// predetermination scrapes. Kept active while SCRAPE_JOBS_ENABLED=False so
// the existing UX continues to work during rollout.
//
// Slated for removal in PR #6 of the concurrent-scrape feature
// (plan: /Users/Admin/.claude/plans/cozy-conjuring-spindle.md). Removal
// requires:
//   1. SCRAPE_JOBS_ENABLED=True permanently in production
//   2. Verified WS reconnection / 2FA-across-pages working in real traffic
//   3. Migration of any callers that still rely on the per-card polling
//      semantics (TreatmentCardStep2.js gating, modal eligibility flows)
// Once those are met, this entire hook can be deleted; the new
// scrapeJobs Redux slice (selectActiveJobsForPolicy) is the replacement.

import { useState, useEffect, useRef } from "react";
import { message } from "antd";

import { controller as predeterminationController } from "../../../controller";
import { controller } from "../controller";
import { debugLog } from "../../../utils/debugLog";
import { useViewerRole } from "../../viewerRole";

export function useGetPatientInsuranceEligibility(
  patientID,
  setShowPatientInsuranceFormModal,
  setShowEligibility2FAFormModal,
  cancelProcess,
  options = {},
) {
  const { onBreakdownEligibilityComplete } = options;
  const lastEligibilityOptsRef = useRef(null);
  // The agent's result toasts are for FTC personnel and super admins; office
  // staff hear about a failure from a person. Read through a ref so the
  // polling effect below keeps its dependency list.
  const viewer = useViewerRole();
  const isFtcRef = useRef(false);
  isFtcRef.current = !!viewer.isFtc;
  const [isGettingEligibilityData, setIsGettingEligibilityData] = useState(false);
  const [taskID, setTaskID] = useState(undefined);
  const [eligibilityAgentToken, setEligibilityAgentToken] = useState(undefined);
  const [isGettingPatientInsuranceEligibility, setIsGettingPatientInsuranceEligibility] = useState(false);
  const [taskStatus, setTaskStatus] = useState(""); // Could be pending | done
  const [agent2FAStatus, setAgent2FAStatus] = useState(""); // Could be pending | waiting | done
  const [eligibilityData, setEligibilityData] = useState(undefined);
  const [breakdownData, setBreakdownData] = useState(undefined);
  const [predeterminationData, setPredeterminationData] = useState(undefined);
  const [predeterminationBatchID, setPredeterminationBatchID] = useState(undefined);
  const [selectedAgentType, setSelectedAgentType] = useState(undefined);
  /** When set, eligibility was started from the breakdown modal (for button loading without toggling parent agentType). */
  const [breakdownModalEligibilityActive, setBreakdownModalEligibilityActive] = useState(false);

  // Eligibility agent - step 1
  async function checkPatientInsuranceEligibility(agentType, policyID, eligibilityOpts, providerIds) {
    if (!policyID) return;

    debugLog.info("legacy_dispatch_started", "useGetPatientInsuranceEligibility", {
      operation_type: agentType,
      policy_id: policyID,
      patient_id: patientID,
    });

    // Reset states to initial values, this is because if the eligiblity agent fail for any reason, then it should start over.
    setAgent2FAStatus("");
    setTaskStatus("");
    setEligibilityAgentToken(undefined);
    setTaskID(undefined);
    setSelectedAgentType(undefined);

    try {
      setIsGettingPatientInsuranceEligibility(true);
      setBreakdownModalEligibilityActive(
        !!(agentType === "eligibility" && eligibilityOpts && eligibilityOpts.isForBreakdown),
      );

      lastEligibilityOptsRef.current = agentType === "eligibility" ? eligibilityOpts || null : null;

      setSelectedAgentType(agentType);
      if (agentType === "eligibility") {
        await startPatientInsuranceEligibilityAgnet(policyID, eligibilityOpts);
      } else if (agentType === "breakdown") {
        await startInsuranceBreakdownAgent(policyID);
      } else if (agentType === "predetermination") {
        await startInsurancePredeterminationAgent(policyID, providerIds);
      }
    } catch (err) {
      message.error("Something went wrong while getting patient insurance status");
      console.error(err);
      setIsGettingPatientInsuranceEligibility(false);
      setBreakdownModalEligibilityActive(false);
    }
  }

  // INFO
  // Step 3 - eligbility, breakdown and predetermination agent
  // Eligibility agent
  async function startPatientInsuranceEligibilityAgnet(policyID, eligibilityOpts) {
    const isBreakdownModalRun = !!(eligibilityOpts && eligibilityOpts.isForBreakdown);
    try {
      const response = await controller.patientAction(policyID, {
        isForBreakdown: isBreakdownModalRun,
        insuranceBreakdownId: eligibilityOpts && eligibilityOpts.insuranceBreakdownId,
      });

      debugLog.info("legacy_dispatch_response", "useGetPatientInsuranceEligibility", {
        operation_type: "eligibility",
        policy_id: policyID,
        http_status: response.status,
        task_id: response.json && response.json.task_id,
        token: response.json && response.json.token,
      });

      if (response.status < 250) {
        const { token, task_id } = response.json || {};

        if (!token || !task_id) {
          message.error("Could not start eligibility agent (missing token or task).");
          setIsGettingPatientInsuranceEligibility(false);
          setBreakdownModalEligibilityActive(false);
          return;
        }

        message.success("Eligbility agent started successfully, This process might take a few minutes, please be patient");

        setTaskID(task_id);
        setEligibilityAgentToken(token);
      } else {
        const errMsg =
          (response.json && (response.json.error || response.json.detail)) || `Request failed (${response.status})`;
        message.error(typeof errMsg === "string" ? errMsg : "Could not start eligibility agent.");
        setIsGettingPatientInsuranceEligibility(false);
        setBreakdownModalEligibilityActive(false);
      }
    } catch (err) {
      message.error("Something went wrong while getting insurance eligibility");
      console.error(err);
      setIsGettingPatientInsuranceEligibility(false);
      setBreakdownModalEligibilityActive(false);
    }
  }

  // Breakdown agent
  async function startInsuranceBreakdownAgent(policyID) {
    try {
      const response = await controller.startBreakdownAgent(policyID);

      debugLog.info("legacy_dispatch_response", "useGetPatientInsuranceEligibility", {
        operation_type: "breakdown",
        policy_id: policyID,
        http_status: response.status,
        task_id: response.json && response.json.task_id,
        token: response.json && response.json.token,
      });

      // console.log(response);
      if (response.status < 250) {
        const { token, task_id } = response.json;

        message.success("Breakdown agent successfully started, This process might take a few minutes, please be patient");

        setTaskID(task_id);
        setEligibilityAgentToken(token);
      } else {
        // The API rejects a policy with no member_id (and no contract_number for
        // SunLife). Without this branch that 400 was swallowed entirely and the
        // user saw nothing at all — the card path already toasts it.
        const detail =
          (response.json && (response.json.error || response.json.detail)) ||
          "Couldn't start the breakdown agent.";
        message.error(detail);
        setIsGettingPatientInsuranceEligibility(false);
      }
    } catch (err) {
      message.error("Something went wrong while getting breakdown data");
      console.error(err);
      setIsGettingPatientInsuranceEligibility(false);
    }
  }

  // Predetermination agent. `providerIds` (optional) fans the fetch out across
  // the selected provider portal accounts — one scrape per account.
  async function startInsurancePredeterminationAgent(policyID, providerIds = []) {
    try {
      const response = await controller.startPredeterminationAgent(policyID, providerIds);

      debugLog.info("legacy_dispatch_response", "useGetPatientInsuranceEligibility", {
        operation_type: "predetermination",
        policy_id: policyID,
        http_status: response.status,
        task_id: response.json && response.json.task_id,
        token: response.json && response.json.token,
      });

      // console.log(response);
      if (response.status < 250) {
        const { token, task_id, file_batch_id } = response.json;

        message.success("Breakdown agent successfully started, This process might take a few minutes, please be patient");

        setTaskID(task_id);
        setEligibilityAgentToken(token);
        setPredeterminationBatchID(file_batch_id);
      } else {
        // Same swallowed-400 problem as the breakdown path above.
        const detail =
          (response.json && (response.json.error || response.json.detail)) ||
          "Couldn't start the estimate agent.";
        message.error(detail);
        setIsGettingPatientInsuranceEligibility(false);
      }
    } catch (err) {
      message.error("Something went wrong while getting predetermination data");
      console.error(err);
      setIsGettingPatientInsuranceEligibility(false);
    }
  }

  // INFO
  // agents - step 4 - Check the task status
  useEffect(
    function () {
      if (cancelProcess || !taskID || agent2FAStatus === "received" || (agent2FAStatus === "not_needed" && taskStatus === "done")) return;

      if (agent2FAStatus === "not_needed" && taskStatus === "failed") {
        if (isFtcRef.current) message.error("Eligibility agent failed, please try again");
        setIsGettingPatientInsuranceEligibility(false);
        setBreakdownModalEligibilityActive(false);
        console.error("Eligibility agent failed, please try again");
        return;
      }

      if (taskStatus === "failed") {
        if (isFtcRef.current) message.error("Eligibility agent failed, please try again");
        setIsGettingPatientInsuranceEligibility(false);
        setBreakdownModalEligibilityActive(false);
        console.error("Eligibility agent failed, please try again");
        return;
      }

      let _taskPollTick = 0;
      async function checkTaskStatusAnd2FA() {
        try {
          // setIsCheckingTaskStatus(true);
          if (taskStatus !== "done") {
            const response = await controller.checkTaskStatus(taskID);

            if (response.status < 250) {
              setTaskStatus(response.json.status);
              _taskPollTick += 1;
              // Log every 5th poll (10s) plus any state change to avoid spam.
              if (_taskPollTick % 5 === 1 || response.json.status !== taskStatus) {
                debugLog.info("legacy_poll_task_status", "useGetPatientInsuranceEligibility", {
                  task_id: taskID,
                  task_status: response.json.status,
                  attempt: _taskPollTick,
                });
              }
            }
          }
        } catch (err) {
          message.error("Something wnet wrong while checking task status");
          console.error(err);
          setIsGettingPatientInsuranceEligibility(false);
          setBreakdownModalEligibilityActive(false);
        }
      }

      const interval = setInterval(checkTaskStatusAnd2FA, 2000);

      return () => clearInterval(interval);
    },
    [taskID, taskStatus, cancelProcess],
  );

  // agents - step 5 - Check if agent is asked for 2fa
  useEffect(
    function () {
      async function check2FA() {
        if (
          cancelProcess ||
          !eligibilityAgentToken ||
          agent2FAStatus === "received" ||
          agent2FAStatus === "not_needed" ||
          taskStatus === "failed" ||
          taskStatus === "done"
        )
          return;

        try {
          // setIsCheckingAgentFor2FA(true);
          const response = await controller.chekcAgentStatusFor2FA(eligibilityAgentToken);

          if (response.status < 250) {
            const rawStatus = response.json && response.json.status;
            const normalized =
              rawStatus == null ? "" : String(rawStatus).trim().toLowerCase();
            const previous = agent2FAStatus;
            setAgent2FAStatus(rawStatus != null ? String(rawStatus) : "");

            // Log on state change to avoid 2s spam.
            if (rawStatus && String(rawStatus) !== previous) {
              debugLog.info("legacy_poll_twofa_status", "useGetPatientInsuranceEligibility", {
                token: eligibilityAgentToken,
                pending_status: String(rawStatus),
              });
            }

            // Backend uses PendingUserInput2.status; "waiting" is set when the portal is ready for a code.
            if (normalized === "waiting" || normalized === "waiting_for_2fa") {
              // Legacy per-card 2FA modal RETIRED: the single app-shell
              // <GlobalScrapeJob2FAModal/> (ScrapeJobs-driven, with the 5-min
              // countdown) is now the only 2FA prompt. Opening the old modal
              // here caused TWO 2FA dialogs to appear at once. We keep polling
              // above to track agent2FAStatus (the poll's stop condition) but no
              // longer pop the deprecated modal.
            }
          }
        } catch (err) {
          message.error("Something went wrong while check for 2fa");
          console.error(err);
          setIsGettingPatientInsuranceEligibility(false);
          setBreakdownModalEligibilityActive(false);
        }
      }

      const interval = setInterval(check2FA, 2000);

      return () => clearInterval(interval);
    },
    [eligibilityAgentToken, agent2FAStatus, cancelProcess, taskStatus],
  );

  // INFO
  // agents - step 7 - Show result data
  useEffect(
    function () {
      if (taskStatus === "done") {
        if (selectedAgentType === "eligibility") {
          const opts = lastEligibilityOptsRef.current;
          if (opts && opts.isForBreakdown && typeof onBreakdownEligibilityComplete === "function") {
            onBreakdownEligibilityComplete();
          }
          getPatientEligibilityInsuranceData();
        } else if (selectedAgentType === "breakdown") {
          getBreakdownData();
        } else if (selectedAgentType === "predetermination") {
          getPredeterminationData();
        }
      }
    },
    [taskStatus],
  );

  // If the process canceled by closing the 2fa or policy info form modals, then stop loading for buttons
  useEffect(
    function () {
      if (cancelProcess) {
        setIsGettingPatientInsuranceEligibility(false);
        setBreakdownModalEligibilityActive(false);
      }
    },
    [cancelProcess],
  );

  // INFO
  async function getPatientEligibilityInsuranceData() {
    try {
      setIsGettingEligibilityData(true);

      const response = await controller.getEligibiltyData(patientID);

      debugLog.info("legacy_result_fetched", "useGetPatientInsuranceEligibility", {
        operation_type: "eligibility",
        patient_id: patientID,
        http_status: response.status,
      });

      if (response.status < 250) {
        setEligibilityData(response.json);
      }
    } catch (err) {
      message.error("Something went wrong while while getting insurance data");
      console.error(err);
    } finally {
      setIsGettingEligibilityData(false);
      setIsGettingPatientInsuranceEligibility(false);
      setBreakdownModalEligibilityActive(false);
    }
  }

  async function getBreakdownData() {
    try {
      setIsGettingEligibilityData(true);

      const response = await controller.getBreakdownDetailByPatient(patientID);

      debugLog.info("legacy_result_fetched", "useGetPatientInsuranceEligibility", {
        operation_type: "breakdown",
        patient_id: patientID,
        http_status: response.status,
      });

      if (response.status < 250) {
        setBreakdownData(response.json);
      }
    } catch (err) {
      message.error("Something went wrong while while getting breakdown data");
      console.error(err);
    } finally {
      setIsGettingEligibilityData(false);
      setIsGettingPatientInsuranceEligibility(false);
    }
  }

  async function getPredeterminationData() {
    try {
      setIsGettingEligibilityData(true);

      const response = await predeterminationController.fetchPredeterminationDetails(predeterminationBatchID);

      debugLog.info("legacy_result_fetched", "useGetPatientInsuranceEligibility", {
        operation_type: "predetermination",
        patient_id: patientID,
        http_status: response.status,
      });

      if (response.status < 250) {
        setPredeterminationData(response.json);
      }
    } catch (err) {
      message.error("Something went wrong while while getting breakdown data");
      console.error(err);
    } finally {
      setIsGettingEligibilityData(false);
      setIsGettingPatientInsuranceEligibility(false);
    }
  }

  // const isLoading = isGettingPatientInsuranceEligibility || isCheckingTaskStatus || isCheckingAgentFor2FA || isGettingEligibilityData;
  const isLoading = isGettingPatientInsuranceEligibility || isGettingEligibilityData;

  return {
    checkPatientInsuranceEligibility,
    isLoading,
    eligibilityAgentToken,
    eligibilityData,
    breakdownData,
    predeterminationData,
    predeterminationBatchID,
    breakdownModalEligibilityActive,
    agent2FAStatus,
    taskStatus,
  };
}
